import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
  type JSX,
} from 'react';

import { translate } from '@vimeo/i18n-runtime';
import { getSatelliteCheckoutParamsFromCurrentLocation } from '@vimeo/picox';

import BpTracking from '../../../utils/bpTracking';
import { PLANS } from '../../../utils/constants';
import { CtaTreatment } from '../../../utils/interface';
import {
  isSeatTierPlan,
  getPlanCTALink,
  appendExtraQueryParams,
  appendTrackingQueryParams,
  appendPaywallParams,
} from '../../../utils/utils';
import TrackingContext from '../TrackingContext';
import { LinkContainer } from './styles';

interface Props {
  allowFreeTrial?: boolean;
  ctaTreatment?: CtaTreatment;
  planData?: any;
  displayMonthlyPlan: boolean;
}

const CTALink = ({
  allowFreeTrial,
  ctaTreatment = 'purchase_secondary',
  planData,
  displayMonthlyPlan,
}: Props) => {
  const ctaText = useRef<string | undefined>(undefined);
  const ctaHref = useRef<string | undefined>(undefined);
  const [ctaComponent, setCtaComponent] = useState<ReactNode>(undefined);

  const trackingProps = useContext(TrackingContext);

  const isEnterprisePlan = planData.tier === PLANS.ENTERPRISE;
  const isFreeTrialLink = ctaTreatment === 'free_trial_secondary' && allowFreeTrial;
  const isPurchaseLink = ctaTreatment === 'purchase_secondary';

  const handleClick = useCallback(() => {
    const { tier, price, currency } = planData;

    // Need to strip internationalization tags for tracking
    const sanitizedCopy = ctaText?.current?.replace(/{\/?A}/g, '').trim();

    BpTracking.trackButtonClick(
      {
        ...trackingProps.params,
        copy: sanitizedCopy,
        currency: currency.currencyCode,
        device_type: trackingProps.device_type,
        duration: displayMonthlyPlan ? 'monthly' : 'annual',
        is_new_pricing: isSeatTierPlan(tier),
        path: window.location.href,
        plan_selected: tier,
        price: displayMonthlyPlan ? price.monthly : price.annualMonthly,
        purchase_type: isFreeTrialLink ? 'trial' : 'direct',
        target: 'checkout',
        target_path: ctaHref.current,
      },
      trackingProps.bpEventVersion,
    );

    if (trackingProps.onButtonClick) {
      trackingProps.onButtonClick({
        copy: sanitizedCopy,
        duration: displayMonthlyPlan ? 'monthly' : 'annual',
        price: displayMonthlyPlan ? price.monthly : price.annualMonthly,
        currency: currency.currencyCode,
        plan_selected: tier,
        target_path: ctaHref.current,
        free_trial: isFreeTrialLink,
      });
    }
  }, [displayMonthlyPlan, isFreeTrialLink, planData, trackingProps]);

  const freeTrialCta = useMemo(
    () =>
      translate({
        singular: 'or {A}start free trial{/A}',
        replacements: {
          A: (children: JSX.Element) => (
            <a
              key="start-fee-trial-link"
              target="_blank"
              href={appendPaywallParams(
                appendExtraQueryParams(
                  appendTrackingQueryParams(
                    planData.metadata.interactions.purchase.uri.freeTrial,
                    trackingProps.params,
                  ),
                  getSatelliteCheckoutParamsFromCurrentLocation(),
                ),
                trackingProps.paywallTracking,
              )}
              onClick={handleClick}
              rel="noreferrer"
            >
              {children}
            </a>
          ),
        },
      }),
    [
      handleClick,
      planData.metadata.interactions.purchase.uri.freeTrial,
      trackingProps.params,
      trackingProps.paywallTracking,
    ],
  );

  const purchaseCta = useMemo(
    () =>
      translate({
        singular: 'or {A}purchase now{/A}',
        replacements: {
          A: (children: JSX.Element) => (
            <a
              key="purchase-now-link"
              target="_blank"
              href={appendPaywallParams(
                appendExtraQueryParams(
                  appendTrackingQueryParams(
                    getPlanCTALink(planData, displayMonthlyPlan, trackingProps.campaignOverride),
                    trackingProps.params,
                  ),
                  getSatelliteCheckoutParamsFromCurrentLocation(),
                ),
                trackingProps.paywallTracking,
              )}
              onClick={handleClick}
              rel="noreferrer"
            >
              {children}
            </a>
          ),
        },
      }),
    [
      handleClick,
      planData,
      displayMonthlyPlan,
      trackingProps.params,
      trackingProps.campaignOverride,
      trackingProps.paywallTracking,
    ],
  );

  useEffect(() => {
    let component = undefined;
    let linkText = '';
    let ctaTarget = '';

    if (isFreeTrialLink) {
      component = freeTrialCta;
      linkText = 'or start free trial';
      ctaTarget = planData.metadata.interactions.purchase.uri.freeTrial;
    } else if (isPurchaseLink) {
      component = purchaseCta;
      linkText = 'or {A}purchase now{/A}';
      ctaTarget = getPlanCTALink(planData, displayMonthlyPlan, trackingProps.campaignOverride);
    }

    ctaText.current = linkText;
    ctaHref.current = ctaTarget;
    setCtaComponent(component);
  }, [isFreeTrialLink, isPurchaseLink, displayMonthlyPlan, planData, freeTrialCta, purchaseCta]);

  return ctaComponent && !isEnterprisePlan ? (
    <LinkContainer data-testid="upsell-cta">{ctaComponent}</LinkContainer>
  ) : (
    <LinkContainer>&nbsp;</LinkContainer>
  );
};

export default CTALink;
